import { AfterViewInit, Component, Input, Output, EventEmitter, OnInit, OnDestroy, ElementRef, signal, inject, DestroyRef, effect } from '@angular/core';
import * as d3 from 'd3';
import { ColorScale, Heatmap } from 'heatmap-component';
import { PlatformService } from '../platform.service';
import { combineLatest, map, take } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { EntryData } from '../entry/structure-data.models';
import { EntrySelectors } from '../state/entry.selectors';
import { Store } from '@ngrx/store';
import { BrushEventValue } from 'heatmap-component/lib/heatmap-component/state';

import { PaeDataService, Chain, PaeMatrix } from './pae-data.service';

export const PaeSelectionColors = {
  X: '#FFA500',
  Y: '#1AFFBB',
  Overlap: '#FFFF00',
};

/** 0-based indices (start and end inclusive) */
export type PaeSelectedRegions = [{ start: number, end: number }, { start: number, end: number }] | undefined;

@Component({
  selector: 'app-pae-view',
  templateUrl: './pae-view.component.html',
  styleUrls: ['./pae-view.component.css'],
  host: {
    '(document:keydown)': 'keyPressListener($event)'
  },
  standalone: false
})
export class PaeViewComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly chainChipPalette = ['#DBBFE3', '#FAE1B6', '#EDB1BB', '#AFD595', '#EBEF95'];

  // @Input() accession: string;
  // @Input() paeDocUrl: string;
  //@Input() imageUrl: string;
  // @Input() length: number;
  @Input() paeid: string;
  @Input() isMobilePae?: boolean;
  @Input() compactChainLabels?: boolean;

  @Output()
  selectedRegion: EventEmitter<PaeSelectedRegions> = new EventEmitter();

  // DOM Elements
  paeImgContainer: d3.Selection<HTMLElement, unknown, HTMLElement, any>;
  public imageUrl: string;
  // D3 scales & Axis
  xAxis: d3.Axis<d3.NumberValue>;
  yAxis: d3.Axis<d3.NumberValue>;
  x: d3.ScaleLinear<number, number>;
  y: d3.ScaleLinear<number, number>;

  // Globals
  brushCtx: any;
  brushSelection: any;
  domainMax: number;
  width: number;
  height: number;
  private _viewInit = false;
  // Heatmap.create() sets up its own ResizeObserver per instance (heatmap-core.js);
  // since this component is destroyed/recreated on every tab switch, a leaked instance
  // here means one more permanently-firing observer on a detached container per revisit,
  // which is what surfaced as repeating `IndexSizeError: ImageData` console spam.
  private heatmapInstance: ReturnType<typeof Heatmap.create> | undefined;
  constructor(
    private el: ElementRef,
    private platform: PlatformService,
    private store: Store,
    private paeDataService: PaeDataService
  ) {
   // keep chains in sync with service
   effect(() => {
    this.proteinChains.set(this.paeDataService.chains());
  });

  // render only when matrix is available AND after view init
  effect(() => {
    const matrix = this.paeMatrix();
    if (matrix && this._viewInit) {
      // call render after both conditions satisfied
      this.renderPae(this.paeid);
      this.renderAxis();
    }
  });
  }
  public accession = signal<string>('');
  public entryData = signal<EntryData>({} as EntryData);
  public proteinChains = signal<Chain[]>([]);
  public paeMatrix = signal<PaeMatrix | undefined>(undefined);

  private readonly destroyRef = inject(DestroyRef);

  ngOnInit() {
    combineLatest([
      this.store.select(EntrySelectors.entryData),
      this.store.select(EntrySelectors.accession)
    ])
      .pipe(
        map(([entryData, accession]) => {
          this.entryData.set(entryData);
          this.accession.set(accession);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(async () => {
        if (this.platform.isBrowserPlatform()) {
  
          // set domain max from entry data
        this.domainMax = this.entryData().sequenceEnd;

        // CORRECT: call fetchPAEMatrix with url (and optional cut) only
        const url = this.entryData().paeDocUrl;
        const matrix = await this.paeDataService.fetchPAEMatrix(url , this.entryData().sequenceEnd/*, optionalCut? */);

        // store into signal so effect picks it up
        this.paeMatrix.set(matrix);

        // keep chains updated (redundant with effect but harmless)
        this.proteinChains.set(this.paeDataService.chains());
        }
      });
  }
  

  ngAfterViewInit() {
    // this image url is for mobile view
    this.imageUrl = this.entryData().paeImageUrl;
    this._viewInit = true;
    // this.renderPae(this.paeid);
    // this.renderAxis();
  }

  async renderPae(divElementOrId: HTMLDivElement | string): Promise<void> {
    const uniprotId = this.accession();
    const pae = this.paeMatrix();
    if (!pae) {
      const msg = `Failed to fetch data for ${uniprotId}.`;
      throw new Error(msg);
    }
    const seqDomain = new Array(pae.n).fill(0).map((_, i) => i + 1);

    // renderPae can re-fire within the same component instance (paeMatrix effect), so
    // dispose of any previous instance before creating a new one, not just on destroy.
    this.heatmapInstance?.remove();

    const heatmap = Heatmap.create<number, number, number>({
      xDomain: seqDomain,
      yDomain: seqDomain,
      data: pae.data,
      x: (d, i) => i % pae.n + 1,
      y: (d, i) => Math.floor(i / pae.n) + 1,
    });
    const colorScale = ColorScale.continuous('Greens', [0, 32], [1, 0]);
    heatmap.setColor(d => colorScale(d));
    heatmap.setTooltip(null);
    heatmap.setVisualParams({ xGapRelative: 0, yGapRelative: 0 });
    heatmap.extensions.marker?.update({ freeze: true });
    if (this.isMobilePae) {
      heatmap.setBrushing({ enabled: false });
    } else {
      heatmap.setBrushing({ enabled: true });
    }
    heatmap.events.brush.subscribe(e => {
      this.updateHighlightLines(e);
      if (e.type === 'end') {
        const selectedRegion: PaeSelectedRegions =
          e.selection ? [
            { start: e.selection.xFirstIndex, end: e.selection.xLastIndex },
            { start: e.selection.yFirstIndex, end: e.selection.yLastIndex },
          ] : undefined;
        this.selectedRegion.emit(selectedRegion);
      }
    });

    heatmap.render(divElementOrId);
    this.heatmapInstance = heatmap;
  }

  ngOnDestroy() {
    this.heatmapInstance?.remove();
    this.heatmapInstance = undefined;
  }

  formatTicks(d: number): any {
    return d > 999 ? d / 1000 + 'k' : d;
  }

  renderAxis() {
    if (this.platform.isBrowserPlatform()) {
      // Selection Image container element
      this.paeImgContainer = d3.select(`.pae-img-box-${this.paeid}`);

      const borderEle = d3.select('.pae-img-box-border');
      this.paeImgContainer.on('mouseover', () => {
        borderEle.attr("class", "pae-img-box-border active");
      });
      this.paeImgContainer.on('mouseout', () => {
        borderEle.attr("class", "pae-img-box-border");
      });

      // SVG addition
      this.dimensionCalc();
      // this.initScales();
      this.updateViewport();
    }
  }

  dimensionCalc() {
    if (this.platform.isBrowserPlatform()) {
      const paeBoxDims = this.paeImgContainer.node().getBoundingClientRect();
      this.width = paeBoxDims.width;
      this.height = paeBoxDims.width;
    }
  }
 
  updateViewport() {
   
    if (this.platform.isBrowserPlatform()) {

      const axisContainer = d3.select(`.pae-axis-box-${this.paeid}`);

      // Clear any previous SVGs
      axisContainer.selectAll("*").remove();

      // --- Compute positions ---
      const proteinChains = this.proteinChains();
      // const proteinChains = [
      //   { name: 'ProteinA', label_asym_id: 'A', length: this.entryData().sequenceEnd },
      // ]
      const totalWidth = this.width;
      const totalHeight = this.height;
      const totalProteins = proteinChains.length;
      const sumDomains = d3.sum(proteinChains.map(c => c.length));
      const offsets = [0];
      for (const chain of proteinChains) {
        offsets.push(offsets[offsets.length - 1] + chain.length);
      }
      const multiChain = proteinChains.length > 1;
      // The fraction of total space each protein occupies
      const scaleFactor = totalWidth / sumDomains;
      const compactLabels = this.compactChainLabels === true;
      const minGap = 10;
      const yMinGap = compactLabels ? 8 : 20;
      const chainLabelFontSize = compactLabels ? 9 : 12;
      const chainChipPadY = compactLabels ? 2 : 3;
      const chainChipPadX = compactLabels ? 5 : 8;
      const chainChipRadius = compactLabels ? 3 : 4;

      const magicYshift = 9;

      // --- Create main SVG containers for x and y axes ---
      const axisSvg = axisContainer.append('svg:svg')
        // .attr('width', 430)
        // .attr('height', 380)
        .attr('class', `pae-axes`)
        // .style('width', '350px')
        // .style('height', '319px')
        // .style('margin-left', '-44px')
        .style('z-index', '1');
      const xAxisSvg = axisSvg
        .append('g')
        .attr('class', 'x-axis')
        .attr('transform', `translate(89,${totalWidth + magicYshift})`);
      const yAxisSvg = axisSvg
        .append('g')
        .attr('class', 'y-axis')
        .attr('transform', `translate(89,${magicYshift})`);
      const gridSvg = axisSvg
        .append('g')
        .attr('class', 'grid')
        .attr('transform', `translate(89,${magicYshift})`);

      yAxisSvg
        .append('text')
        .text('Aligned residue')
        .attr('text-anchor', 'middle')
        .attr('font-weight', 500)
        .attr('class','pae-x-lable')
        // .attr('transform', `translate(-70,${totalWidth / 2}) rotate(-90)`);

      // --- Draw concatenated axes ---
      const OPTIMAL_N_TICKS = 25 / (Math.max(...proteinChains.map(c => c.length)).toString().length + 0.5);
      const TICK_HIDE_FACTOR = 0.25;
      const tickStep = d3.tickStep(0, sumDomains, OPTIMAL_N_TICKS);

      function globalTickFormat(_: unknown, i: number) {
        // if (multiChain) {
        //   if (i === 0) return `0`;
        //   if (i === offsets.length - 1) return `${proteinChains[i - 1].length}`;
        //   return `${proteinChains[i - 1].length}/0`;
        // } else {
        //   if (i === 0) return `0`;
        //   else return `${proteinChains[0].length}`;
        // }
        if (i === 0) return `0`;
        if (i === offsets.length - 1) return `${proteinChains[i - 1].length}`;
        return `${proteinChains[i - 1].length}/0`;
      }
      const globalScale = d3.scaleLinear()
        .domain([0, sumDomains])
        .range([0, sumDomains * scaleFactor]);

      const globalXAxis = d3.axisBottom(globalScale)
        .tickValues(offsets)
        .tickSize(16)
        .tickFormat(globalTickFormat);

      const globalYAxis = d3.axisLeft(globalScale)
        .tickValues(offsets)
        .tickSize(16)
        .tickFormat(globalTickFormat);

      xAxisSvg.append('g').call(globalXAxis);
      yAxisSvg.append('g').call(globalYAxis);

      // Dynamic spacing based on rendered tick label bounds keeps layout readable for longer numbers.
      let xTickLabelBottom = 0;
      xAxisSvg.selectAll<SVGTextElement, unknown>('.tick text').each(function () {
        const bbox = this.getBBox();
        xTickLabelBottom = Math.max(xTickLabelBottom, bbox.y + bbox.height);
      });
      const xChainLabelY = xTickLabelBottom + minGap + chainLabelFontSize / 2;
      const hasVisibleChainLabels = multiChain && !this.isMobilePae;
      const defaultXAxisTitleY = xChainLabelY + chainLabelFontSize / 2 + chainChipPadY * 2 + minGap;
      const xAxisTitleY = this.isMobilePae ? xTickLabelBottom + 8 : defaultXAxisTitleY;

      let yTickLabelRight = Number.NEGATIVE_INFINITY;
      yAxisSvg.selectAll<SVGTextElement, unknown>('.tick text').each(function () {
        const bbox = this.getBBox();
        yTickLabelRight = Math.max(yTickLabelRight, bbox.x + bbox.width);
      });
      const yChainLabelX = yTickLabelRight - yMinGap - chainLabelFontSize / 2;

      xAxisSvg
        .append('text')
        .text('Scored residue')
        .attr('text-anchor', 'middle')
        .attr('font-weight', 500)
        .attr('transform', `translate(${totalWidth / 2},${xAxisTitleY})`);

      // Show chain labels between x-axis ticks and the axis title for multi-chain models.
      if (hasVisibleChainLabels) {
        const xChainLabels = xAxisSvg.append('g')
          .attr('class', 'x-chain-labels')
          .selectAll<SVGTextElement, { chain: Chain; i: number }>('text')
          .data(proteinChains.map((chain, i) => ({ chain, i })))
          .enter()
          .append('text')
          .attr('class', 'pae-chain-label')
          .attr('fill', '#1a1c1a')
          .attr('font-size', chainLabelFontSize)
          .attr('font-weight', 500)
          .attr('x', ({ chain, i }) => {
            const start = offsets[i];
            const end = start + chain.length;
            return globalScale((start + end) / 2);
          })
          .attr('y', xChainLabelY)
          .attr('text-anchor', 'middle')
          .text(({ chain, i }) => chain.label_asym_id ? `Chain ${chain.label_asym_id}` : `Chain ${i + 1}`);

        const yChainLabels = yAxisSvg.append('g')
          .attr('class', 'y-chain-labels')
          .selectAll<SVGTextElement, { chain: Chain; i: number }>('text')
          .data(proteinChains.map((chain, i) => ({ chain, i })))
          .enter()
          .append('text')
          .attr('class', 'pae-chain-label-y')
          .attr('fill', '#1a1c1a')
          .attr('font-size', chainLabelFontSize)
          .attr('font-weight', 500)
          .attr('x', yChainLabelX)
          .attr('y', ({ chain, i }) => {
            const start = offsets[i];
            const end = start + chain.length;
            return globalScale((start + end) / 2);
          })
          .attr('text-anchor', 'middle')
          .attr('transform', (_d, i, nodes) => {
            const node = nodes[i] as SVGTextElement;
            const y = Number(node.getAttribute('y') || 0);
            return `rotate(-90, ${yChainLabelX}, ${y})`;
          })
          .text(({ chain, i }) => chain.label_asym_id ? `Chain ${chain.label_asym_id}` : `Chain ${i + 1}`);

        this.addChainLabelChips(xChainLabels, chainChipPadX, chainChipPadY, chainChipRadius);
        this.addChainLabelChips(yChainLabels, chainChipPadX, chainChipPadY, chainChipRadius);
      }

      if (multiChain) {
        gridSvg.append('g').attr('class', 'x-grid')
          .selectAll('line').data(offsets)
          .enter()
          .append('line')
          .attr('stroke', 'black')
          .attr('x1', globalScale).attr('x2', globalScale)
          .attr('y1', 0).attr('y2', totalWidth);
        gridSvg.append('g').attr('class', 'y-grid')
          .selectAll('line').data(offsets)
          .enter()
          .append('line')
          .attr('stroke', 'black')
          .attr('x1', 0).attr('x2', totalWidth)
          .attr('y1', globalScale).attr('y2', globalScale);
      }

      proteinChains.forEach((chain, i) => {
        const offset = offsets[i];
        // Individual local scales for each protein
        const localScale = d3.scaleLinear()
          .domain([0, chain.length])
          .range([offset * scaleFactor, (offset + chain.length) * scaleFactor]);

        const tickValues = d3.range(0, chain.length, tickStep).slice(1, undefined);
        function tickFormat(d: number) {
          if (d > chain.length - TICK_HIDE_FACTOR * tickStep) return '';
          return `${d}`;
        }

        // Local axes
        const localXAxis = d3.axisBottom(localScale)
          .tickValues(tickValues)
          .tickFormat(tickFormat)
          .tickSize(4)
          .tickSizeOuter(0);

        const localYAxis = d3.axisLeft(localScale)
          .tickValues(tickValues)
          .tickFormat(tickFormat)
          .tickSize(4)
          .tickSizeOuter(0);

        // Append each local X and Y axis
        xAxisSvg.append('g').call(localXAxis);
        yAxisSvg.append('g').call(localYAxis);
      });
    }
  }

  private addChainLabelChips(
    labels: d3.Selection<SVGTextElement, { chain: Chain; i: number }, SVGGElement, unknown>,
    chipPadX: number,
    chipPadY: number,
    chipRadius: number
  ): void {

    labels.each((d, _index, nodes) => {
      const node = nodes[_index] as SVGTextElement;
      const chainColor = this.chainChipPalette[d.i % this.chainChipPalette.length];
      const text = d3.select(node);
      const parent = d3.select(node.parentNode as SVGGElement);
      const bbox = node.getBBox();
      const textTransform = text.attr('transform');

      const chip = parent
        .insert('rect', () => node)
        .attr('class', 'pae-chain-chip')
        .attr('fill', chainColor)
        .attr('stroke', chainColor)
        .attr('stroke-opacity', 0.45)
        .attr('stroke-width', 1)
        .attr('x', bbox.x - chipPadX)
        .attr('y', bbox.y - chipPadY)
        .attr('width', bbox.width + chipPadX * 2)
        .attr('height', bbox.height + chipPadY * 2)
        .attr('rx', chipRadius)
        .attr('ry', chipRadius);
        // .attr('fill-opacity', 0.22)
      if (textTransform) {
        chip.attr('transform', textTransform);
      }
    });
  }

  updateHighlightLines(brushEvent: BrushEventValue<number, number>) {
    const gridSvg = d3.select(`.pae-axis-box-${this.paeid}`).select('.grid');
    gridSvg.selectAll('.highlight-line, .helper-line').remove();

    if (!brushEvent.selection) return; // empty selection

    const sumDomains = d3.sum(this.proteinChains().map(c => c.length));
    const globalScale = d3.scaleLinear([0, sumDomains], [0, this.width]);

    const xStart = globalScale(brushEvent.selection.xMinIndex);
    const xEnd = globalScale(brushEvent.selection.xMaxIndex);
    const yStart = globalScale(brushEvent.selection.yMinIndex);
    const yEnd = globalScale(brushEvent.selection.yMaxIndex);
    const overlapStart = Math.max(xStart, yStart);
    const overlapEnd = Math.min(xEnd, yEnd);

    if (xStart === xEnd && yStart === yEnd) return; // starting brush gesture

    function addHighlightLine(color: string) {
      return gridSvg.append('line')
        .attr('class', 'highlight-line')
        .attr('stroke', color)
        .attr('stroke-width', 3)
        .attr('stroke-linecap', 'round');
    }
    function addHelperLine() {
      return gridSvg.append('line')
        .attr('class', 'helper-line')
        .attr('stroke', 'white')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '5 3');
    }

    /** This gap is mostly to avoid collision with the selection close button (X) */
    const GAP = 4;

    // Vertical helper lines
    if (xStart < yStart) addHelperLine().attr('x1', xStart).attr('y1', yStart - GAP).attr('x2', xStart).attr('y2', xStart + GAP);
    if (xStart > yEnd) addHelperLine().attr('x1', xStart).attr('y1', yEnd + GAP).attr('x2', xStart).attr('y2', xStart - GAP);
    if (xEnd < yStart) addHelperLine().attr('x1', xEnd).attr('y1', yStart - GAP).attr('x2', xEnd).attr('y2', xEnd + GAP);
    if (xEnd > yEnd) addHelperLine().attr('x1', xEnd).attr('y1', yEnd + GAP).attr('x2', xEnd).attr('y2', xEnd - GAP);

    // Horizontal helper lines
    if (yStart < xStart) addHelperLine().attr('x1', xStart - GAP).attr('y1', yStart).attr('x2', yStart + GAP).attr('y2', yStart);
    if (yStart > xEnd) addHelperLine().attr('x1', xEnd + GAP).attr('y1', yStart).attr('x2', yStart - GAP).attr('y2', yStart);
    if (yEnd < xStart) addHelperLine().attr('x1', xStart - GAP).attr('y1', yEnd).attr('x2', yEnd + GAP).attr('y2', yEnd);
    if (yEnd > xEnd) addHelperLine().attr('x1', xEnd + GAP).attr('y1', yEnd).attr('x2', yEnd - GAP).attr('y2', yEnd);

    // Diagonal highlight lines
    addHighlightLine(PaeSelectionColors.X).attr('x1', xStart).attr('y1', xStart).attr('x2', xEnd).attr('y2', xEnd);
    addHighlightLine(PaeSelectionColors.Y).attr('x1', yStart).attr('y1', yStart).attr('x2', yEnd).attr('y2', yEnd);
    if (overlapStart <= overlapEnd) addHighlightLine(PaeSelectionColors.Overlap).attr('x1', overlapStart).attr('y1', overlapStart).attr('x2', overlapEnd).attr('y2', overlapEnd);
  }


  toggleRemoveIcon(hide?: boolean) {

    if (this.platform.isBrowserPlatform()) {
      const closeIconElm = this.el.nativeElement.querySelector('.pae-clear-icon');
      if (hide) {
        closeIconElm.style.display = 'none';
      } else {

        const selectionBoxElm = this.el.nativeElement.querySelector('.selection');
        if (!selectionBoxElm || selectionBoxElm.width.baseVal.value === 0) return;

        const leftAdjustVal = selectionBoxElm.width.baseVal.value > 20 ? 9 : 0;
        let leftMargin = selectionBoxElm.x.baseVal.value + selectionBoxElm.width.baseVal.value - leftAdjustVal;
        let topMargin = selectionBoxElm.y.baseVal.value - 9;

        if (leftMargin >= this.width - 15) {
          leftMargin -= 10;
        }

        if (selectionBoxElm.y.baseVal.value <= 15) {
          topMargin = 2;
        }

        closeIconElm.style.marginLeft = leftMargin + 'px';
        closeIconElm.style.marginTop = topMargin + 'px';
        closeIconElm.style.display = 'inline-block';

      }
    }
  }

  keyPressListener(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.clearSelection();
    }
  }

  clearSelection() {
    this.brushSelection.call(this.brushCtx.move, null);
  }

}
